import React from "react";
import { Helmet } from "react-helmet-async";

const SITE = {
  name: "Dead Bug Edwards",
  url: "https://dbe.bp.agency",
  defaultImage: "https://customer-assets-cm19k8pv.emergentagent.net/job_deadbug-leads/artifacts/y6gydevs_DBE.jpg",
  twitter: "@deadbugedwards",
};

/**
 * <Seo /> - Per-page SEO tags + optional JSON-LD structured data blocks.
 *
 * Props:
 *   title           string  Page-specific title (will be suffixed with brand)
 *   description     string  Meta description
 *   canonical       string  Canonical path (e.g. "/services/termite") - full URL is derived
 *   image           string  Open Graph / Twitter card image URL
 *   jsonLd          object|array  One or more structured-data objects to embed as JSON-LD
 *   noIndex         bool    If true, adds robots noindex
 */
export default function Seo({
  title,
  description,
  canonical,
  image,
  jsonLd,
  noIndex = false,
}) {
  const fullTitle = title
    ? `${title} | ${SITE.name}`
    : `${SITE.name} - Termite & Pest Control in South Florida Since 1959`;
  const desc =
    description ||
    "Dead Bug Edwards is South Florida's family-owned termite and pest control company - trusted since 1959. Serving Broward, Miami-Dade & Palm Beach. Call 1-800-DEAD BUG for a free estimate.";
  const url = `${SITE.url}${canonical || "/"}`;
  const img = image || SITE.defaultImage;

  const ldArray = jsonLd
    ? Array.isArray(jsonLd)
      ? jsonLd
      : [jsonLd]
    : [];

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={url} />
      {noIndex && <meta name="robots" content="noindex,nofollow" />}
      {!noIndex && <meta name="robots" content="index,follow,max-image-preview:large,max-snippet:-1" />}

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE.name} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={img} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={img} />

      {/* JSON-LD */}
      {ldArray.map((obj, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}
    </Helmet>
  );
}

// ---------- JSON-LD helpers ----------
export const localBusinessLd = {
  "@context": "https://schema.org",
  "@type": ["LocalBusiness", "PestControlService"],
  "@id": `${SITE.url}#organization`,
  name: SITE.name,
  alternateName: "Dead Bug Edwards - The Termite Company",
  description:
    "Family-owned termite and pest control company serving South Florida since 1959. Termite, general pest control, rodent and marine pest services.",
  url: SITE.url,
  telephone: "+1-800-332-3284",
  email: "info@deadbugedwards.net",
  image: SITE.defaultImage,
  logo: "https://customer-assets.emergentagent.com/job_deadbug-leads/artifacts/a2dva2pd_logo-3.webp",
  priceRange: "$$",
  foundingDate: "1959",
  address: {
    "@type": "PostalAddress",
    streetAddress: "300 S State Road 7",
    addressLocality: "Plantation",
    addressRegion: "FL",
    postalCode: "33317",
    addressCountry: "US",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: 26.1039,
    longitude: -80.2028,
  },
  areaServed: [
    { "@type": "City", name: "Plantation" },
    { "@type": "City", name: "Fort Lauderdale" },
    { "@type": "City", name: "Weston" },
    { "@type": "City", name: "Davie" },
    { "@type": "City", name: "Sunrise" },
    { "@type": "City", name: "Pembroke Pines" },
    { "@type": "City", name: "Miramar" },
    { "@type": "City", name: "Coral Springs" },
    { "@type": "City", name: "Hollywood" },
    { "@type": "City", name: "Miami" },
    { "@type": "City", name: "Boca Raton" },
    { "@type": "City", name: "Delray Beach" },
    { "@type": "City", name: "Deerfield Beach" },
    { "@type": "City", name: "Pompano Beach" },
    { "@type": "City", name: "Coconut Creek" },
  ],
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "08:00",
      closes: "17:00",
    },
  ],
  sameAs: [],
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "5.0",
    reviewCount: "125",
  },
};

export const websiteLd = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE.url}#website`,
  url: SITE.url,
  name: SITE.name,
  publisher: { "@id": `${SITE.url}#organization` },
  inLanguage: "en-US",
};

export const breadcrumbLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, idx) => ({
    "@type": "ListItem",
    position: idx + 1,
    name: it.name,
    item: `${SITE.url}${it.path}`,
  })),
});

export const serviceLd = (service) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: service.title,
  name: `${service.title} in South Florida`,
  description: service.heroSub || service.blurb,
  provider: { "@id": `${SITE.url}#organization` },
  areaServed: {
    "@type": "AdministrativeArea",
    name: "South Florida",
  },
  offers: {
    "@type": "Offer",
    availability: "https://schema.org/InStock",
    priceCurrency: "USD",
    priceSpecification: {
      "@type": "PriceSpecification",
      price: "0",
      priceCurrency: "USD",
      description: "Free estimate",
    },
  },
  url: `${SITE.url}/services/${service.slug}`,
  image: service.img,
});

export const SITE_URL = SITE.url;
